import React from 'react'
import { Link } from 'react-router-dom'
import { useSEO } from '../hooks/useSEO'
import { ContactForm } from '../components/ContactForm'
import { useCMSSide } from '../hooks/useCMS'

export function CoachingPage() {
  const { side: cmsSide } = useCMSSide('coaching')

  useSEO({
    title: cmsSide?.seo_titel || 'Coaching - BoSteendahl.dk',
    description: cmsSide?.seo_beskrivelse || 'Mindset coaching og NLP ved Bo Steendahl. Styrk kontakten mellem krop og psyke og frisæt dine skjulte ressourcer i et trygt forløb.',
    ogImage: cmsSide?.dele_billede?.[0] || 'https://assets.auxodev.dk/assets/2a253654-8b0d-44c3-813e-14453c64ff7c/20220903-135741.jpg',
  })

  return (
    <div className="bg-[#FAF8F5]">
      {/* Hero Section */}
      <section className="relative py-16 lg:py-24 bg-gradient-to-b from-[#F5EFE9] to-[#FAF8F5]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#1b3a4b]/8 text-[#1b3a4b] text-xs font-semibold uppercase tracking-wider mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-[#7c875a]" />
            Kerneområde · Mindset Coaching &amp; NLP
          </div>
          <h1 className="font-serif text-[32px] sm:text-5xl lg:text-6xl font-bold text-[#1b3a4b] tracking-tight">
            {cmsSide?.hero_overskrift || 'Coaching'}
          </h1>
          <p className="mt-4 text-xl sm:text-2xl font-serif text-[#1b3a4b]/90 max-w-3xl mx-auto leading-relaxed">
            {cmsSide?.hero_underoverskrift || 'Kontakten mellem hoved og krop er afgørende for vores relation til os selv og til verden omkring os.'}
          </p>
        </div>
      </section>

      {/* Forløbs-princip Box */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 -mt-8 relative z-20">
        <div className="bg-[#172e3b] text-white rounded-2xl p-8 sm:p-10 shadow-xl border border-white/10">
          <div className="flex items-center gap-3 text-[#7c875a] font-semibold text-xs uppercase tracking-wider mb-2">
            <i className="fa-solid fa-compass" aria-hidden="true" />
            <span>Coaching som del af forløb</span>
          </div>
          <h2 className="font-serif text-[21px] sm:text-3xl font-bold text-white mb-3">
            Mindset Coaching i sammenhæng med kroppen
          </h2>
          <p className="text-[#FAF8F5]/90 text-sm sm:text-base leading-relaxed">
            Coaching hos Bo Steendahl er ikke en isoleret 'snakketime', men en integreret del af et behandlingsforløb. Vi arbejder med samspillet mellem hvad kroppen mærker, og hvordan sindet og sproget former dine handlinger og vaner.
          </p>
        </div>
      </section>

      {/* Main Content with Aside Form */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            {/* Main Content Column */}
            <div className="lg:col-span-7 xl:col-span-8 space-y-10">
              {/* Tanker og krop */}
              <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm border border-[#E5E0D8] space-y-4">
                <span className="text-xs font-semibold tracking-wider text-[#7c875a] uppercase block">
                  Fra hovedet ned i kroppen
                </span>
                <h3 className="font-serif text-2xl sm:text-3xl font-bold text-[#1b3a4b]">
                  Det meste af vores liv foregår i hovedet
                </h3>
                <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
                  Stress, forventninger og pres udefra, såvel som indefra, fylder op. Vi er fremmedgjort fra vores krop, og vi glemmer at mærke efter. Det vil jeg gerne hjælpe dig med.
                </p>
                <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
                  I mit arbejde med kropsterapi og coaching vil jeg hjælpe dig med at skabe bedre kontakt mellem dine tanker, dit sind og din krop. Uanset hvor uoverskueligt livet er lige nu, har du indeni de ressourcer som skal til for at blive bedre til at mærke efter og tage hånd om og give udtryk for dine behov, og lære at stå ved den du er.
                </p>
                <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
                  Jeg vil hjælpe dig med at finde ind til din indre styrke og sætte energien fri, så du kan blive den bedste version af dig selv til glæde for dem du ønsker at være noget for, og for dig selv – den vigtigste person i dit liv.
                </p>
              </div>

              {/* NLP & Den fundamentale dynamik */}
              <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm border border-[#E5E0D8] space-y-4">
                <span className="text-xs font-semibold tracking-wider text-[#7c875a] uppercase block">
                  Baggrund og metode
                </span>
                <h3 className="font-serif text-2xl sm:text-3xl font-bold text-[#1b3a4b]">
                  NLP og samspillet mellem sind, sprog og adfærd
                </h3>
                <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
                  Fra min tid i Norge og Sverige, hvor jeg mødte Svein — som gennem mange år havde arbejdet med og undervist i NLP (Neuro Lingvistisk Programmering) — lærte jeg om den fundamentale dynamik mellem sind og sprog, og hvordan samspillet påvirker vores krop, adfærd og tanker.
                </p>
                <blockquote className="border-l-4 border-[#7c875a] bg-[#FAF8F5] p-5 rounded-r-lg text-sm sm:text-base italic text-[#334155] leading-relaxed">
                  I min behandling bruger jeg ofte coaching til at synliggøre de ressourcer som ligger gemt under overfladen. Kombinationen af den fysiske behandling og coaching til at mærke efter inden i, åbner op og nye indsigter på egne kompetencer og muligheder kommer op til overfladen.
                </blockquote>
                <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
                  Undervejs i behandlingen kommer vi ind på hvad det er som begrænser os i vores liv, og erkendelsens lys bliver tændt når du mærker ressourcer som du ikke tidligere har været bevidst om. Ressourcer som du på sin vis godt vidste du besidder, men aldrig rigtig har levet ud.
                </p>
              </div>

              {/* Klientens feedback på coaching */}
              <div className="bg-[#1b3a4b] text-white rounded-2xl p-8 sm:p-10 shadow-lg space-y-4">
                <span className="text-xs font-semibold tracking-wider text-[#7c875a] uppercase block">
                  Klienterfaring med coaching
                </span>
                <h3 className="font-serif text-2xl font-bold text-white">
                  Hvad coaching i forløbet satte i gang
                </h3>
                <p className="text-sm sm:text-base text-[#FAF8F5]/90 italic leading-relaxed">
                  "Mht. at du synliggør ens egne ressourcer er det helt vildt hvad det satte gang i hos mig. Nogle tanker nåede jeg at dele med dig, men faktum er at jeg har tilmeldt mig NLP psykoterapeut i Aarhus... Forandringer, ja vel - og ja tak til selvudvikling og papir på det jeg kan. Jeg har en stor taknemmelighed til dig."
                </p>
                <span className="text-xs text-[#7c875a] block font-semibold">— Charlotte Hoé (Klient)</span>
              </div>
            </div>

            {/* Aside Contact Form */}
            <aside className="lg:col-span-5 xl:col-span-4 lg:sticky lg:top-24 space-y-6">
              <ContactForm />
              <div className="bg-white rounded-2xl p-5 border border-[#E5E0D8] text-xs text-[#475569] space-y-2.5 shadow-sm">
                <div className="font-semibold text-[#1b3a4b] text-sm">Direkte kontakt</div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-location-dot text-[#7c875a]" aria-hidden="true" />
                  <span>Vendersgade 37, 7000 Fredericia</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-phone text-[#7c875a]" aria-hidden="true" />
                  <a href="tel:53128213" className="hover:text-[#1b3a4b] font-medium">Tlf: 53 12 82 13</a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  )
}
